import { useCallback } from 'react';
import { useStore } from 'zustand';
import { useSimpleDragManager } from '../components/svgElements/SimpleDragManager';
import { useUnityBuildStore } from '../stores/store';
import { useAssemblyStore } from '../stores/AssemblyStore';
import { useSubmitTakeoffAssembly } from './useSubmitTakeoffAssembly';
import { TakeoffAssembly } from '../types/AssemblyItemType';
import { ResetStates } from '../stores/resetStates';
import { useKeyBoardEventStore } from '../stores/keyEventStore';
import { useFetchCountedAssemblies } from './useFetchCountedAssemblies';

/**
 * Hook to handle drag end operations
 * Batches updates and submits to backend efficiently
 */
export const useDragEndHandler = () => {
  const { endDrag } = useSimpleDragManager();
  const { userScale } = useStore(useUnityBuildStore);
  const { data: filteredCountedAssemblies } = useFetchCountedAssemblies();
  // Removed unused currentTakeoffAssembly and setCurrentTakeoffAssembly
  const { submitAssembly } = useSubmitTakeoffAssembly();
  const unityBuildStore = useStore(useUnityBuildStore);
  const globalAssemblyStore = useStore(useAssemblyStore);
  const keyboardEventStore = useStore(useKeyBoardEventStore);

  const handleDragEnd = useCallback(async () => {
    // Get final positions from drag manager
    const finalPositions = endDrag();
    if (finalPositions.length === 0) return;

    try {
      // Group points by assembly for batch updates
      const assemblyUpdates = new Map<string, TakeoffAssembly>();

      // Process each moved point
      finalPositions.forEach(({ pointId, assemblyId, finalX, finalY }) => {
        // Find the assembly containing this point (current page only)
        const assemblies = Array.isArray(filteredCountedAssemblies)
          ? []
          : filteredCountedAssemblies?.filteredAssemblies?.flatMap(
              (group: any) => group.assembliesList || []
            ) || [];

        const assembly = assemblies.find((asm: any) => asm.assemblyid === assemblyId);

        if (!assembly) return;

        // Get or create the updated assembly
        let updatedAssembly = assemblyUpdates.get(assembly.assemblyid);
        if (!updatedAssembly) {
          updatedAssembly = {
            ...assembly,
            pointsList: [...(assembly.pointsList || [])],
          };
          assemblyUpdates.set(assembly.assemblyid, updatedAssembly!);
        }

        // Update the point position (updatedAssembly is guaranteed to exist at this point)
        const pointIndex = updatedAssembly!.pointsList.findIndex(pt => pt.pointid === pointId);
        if (pointIndex !== -1) {
          updatedAssembly!.pointsList[pointIndex] = {
            ...updatedAssembly!.pointsList[pointIndex],
            x: finalX,
            y: finalY,
          };
        }
      });

      // Fire-and-forget updates for optimistic UX
      Array.from(assemblyUpdates.values()).forEach(assembly => {
        void submitAssembly({
          shouldUpdateBreakout: false,
          assembly,
          isUpdating: true,
          isCreating: false,
          pageScale: userScale,
        });
      });

      // Don't reset states immediately - let the optimistic cache handle the transition
      // Only clear currentTakeoffAssembly to prevent conflicts with the updated cache
      globalAssemblyStore.setCurrentTakeoffAssembly(null);
    } catch (error) {
      console.error('Error updating points after drag:', error);
      // Reset states even on error
      ResetStates.resetTakeoffStates(unityBuildStore, globalAssemblyStore, keyboardEventStore);
    }
  }, [
    endDrag,
    filteredCountedAssemblies,
    submitAssembly,
    userScale,
    unityBuildStore,
    globalAssemblyStore,
    keyboardEventStore,
  ]);

  return { handleDragEnd };
};
